import { computed } from "vue";

/**
 * KPIs de variación de la evolución del dashboard.
 * Extrae revenue_change_pct y attendance_change_pct de vs_previous_season.
 *
 * @param {import('vue').ComputedRef<object|null>} currentSeasonEntryRef — computed de la entrada de temporada actual
 * @param {import('vue').ComputedRef<boolean>} isMultiSeasonRef — computed que indica multi-temporada
 * @returns {{
 *     kpis: import('vue').ComputedRef<{ revenue: object, attendance: object }|null>,
 *     revenueChange: import('vue').ComputedRef<number>,
 *     attendanceChange: import('vue').ComputedRef<number>,
 * }}
 */
export function useEvolutionKpis(currentSeasonEntryRef, isMultiSeasonRef) {
    /**
     * KPIs de variación respecto a la temporada anterior.
     * @type {import('vue').ComputedRef<{ revenue: object, attendance: object }|null>}
     */
    const kpis = computed(() => {
        if (isMultiSeasonRef.value) return null;

        const entry = currentSeasonEntryRef.value;
        if (!entry) return null;

        const vs = entry.vs_previous_season;
        if (!vs || typeof vs !== "object") return null;

        const revenueChangePct =
            typeof vs.revenue_change_pct === "number" ? vs.revenue_change_pct : 0;
        const attendanceChangePct =
            typeof vs.attendance_change_pct === "number"
                ? vs.attendance_change_pct
                : 0;

        return {
            revenue: {
                current:
                    typeof vs.revenue_current === "number" ? vs.revenue_current : 0,
                previous:
                    typeof vs.revenue_previous === "number" ? vs.revenue_previous : 0,
                changePct: revenueChangePct,
            },
            attendance: {
                current:
                    typeof vs.attendance_current === "number" ? vs.attendance_current : 0,
                previous:
                    typeof vs.attendance_previous === "number"
                        ? vs.attendance_previous
                        : 0,
                changePct: attendanceChangePct,
            },
        };
    });

    /**
     * Variación porcentual de ingresos respecto a la temporada anterior.
     * @type {import('vue').ComputedRef<number>}
     */
    const revenueChange = computed(() => {
        if (isMultiSeasonRef.value) return 0;
        const entry = currentSeasonEntryRef.value;
        if (!entry?.vs_previous_season) return 0;
        return typeof entry.vs_previous_season.revenue_change_pct === "number"
            ? entry.vs_previous_season.revenue_change_pct
            : 0;
    });

    /**
     * Variación porcentual de asistencia respecto a la temporada anterior.
     * @type {import('vue').ComputedRef<number>}
     */
    const attendanceChange = computed(() => {
        if (isMultiSeasonRef.value) return 0;
        const entry = currentSeasonEntryRef.value;
        if (!entry?.vs_previous_season) return 0;
        return typeof entry.vs_previous_season.attendance_change_pct === "number"
            ? entry.vs_previous_season.attendance_change_pct
            : 0;
    });

    return {
        kpis,
        revenueChange,
        attendanceChange,
    };
}
